import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { networkManager } from '../api/NetworkManager';
import { getCurrentUser } from '../api';

interface EmulationInfo {
  originalUser: { sub: string; username?: string; email?: string; displayName?: string };
  targetYumId: string;
}

interface User {
  sub: string;
  email?: string;
  username?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  groups: string[];
  roles: string[];
  capabilities: string[];
  brands?: string[];
  overrides?: { additions: string[]; removals: string[] };
  emulating?: EmulationInfo;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  setTokens: (idToken: string, refreshToken: string) => void;
  /** Check capability triple with wildcard support: can('prod', 'stores', 'read') */
  can: (env: string, domain: string, action: string) => boolean;
  /** Whether currently emulating another user */
  isEmulating: boolean;
  /** Start emulating a user by YUM ID */
  startEmulation: (yumId: string) => Promise<void>;
  /** Stop emulating and return to superadmin view */
  stopEmulation: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

const TOKEN_KEY = 'byte_id_token';
const REFRESH_TOKEN_KEY = 'byte_refresh_token';
const EMULATE_KEY = 'byte_emulate_user';

/**
 * Check if user capabilities match a required {env}:{domain}:{action} triple.
 * Supports wildcard matching: '*' matches any value in that position.
 */
function hasEnvCapability(
  capabilities: string[],
  env: string,
  domain: string,
  action: string,
): boolean {
  for (const cap of capabilities) {
    const [capEnv, capDomain, capAction] = cap.split(':');
    const envMatch = capEnv === '*' || capEnv === env;
    const domainMatch = capDomain === '*' || capDomain === domain;
    const actionMatch = capAction === '*' || capAction === action;
    if (envMatch && domainMatch && actionMatch) {
      return true;
    }
  }
  return false;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [emulatingYumId, setEmulatingYumId] = useState<string | null>(() =>
    sessionStorage.getItem(EMULATE_KEY),
  );

  const setTokens = useCallback((idToken: string, refreshToken: string) => {
    localStorage.setItem(TOKEN_KEY, idToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    networkManager.setToken(idToken);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    networkManager.setToken(null);
    setUser(null);
  }, []);

  const login = useCallback(() => {
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    const redirectUri =
      import.meta.env.VITE_COGNITO_REDIRECT_URI || `${window.location.origin}/auth/callback`;

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: redirectUri,
    });

    window.location.href = `${cognitoDomain}/oauth2/authorize?${params}`;
  }, []);

  const can = useCallback(
    (env: string, domain: string, action: string) => {
      if (!user?.capabilities) return false;
      return hasEnvCapability(user.capabilities, env, domain, action);
    },
    [user],
  );

  const stopEmulation = useCallback(() => {
    networkManager.setEmulateUser(null);
    sessionStorage.removeItem(EMULATE_KEY);
    setEmulatingYumId(null);
    // Re-fetch /me to get original capabilities
    getCurrentUser()
      .then(setUser)
      .catch(() => logout());
  }, [logout]);

  const startEmulation = useCallback(
    async (yumId: string) => {
      networkManager.setEmulateUser(yumId);
      sessionStorage.setItem(EMULATE_KEY, yumId);
      setEmulatingYumId(yumId);
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch {
        stopEmulation();
      }
    },
    [stopEmulation],
  );

  // Restore session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (storedToken) {
      networkManager.setToken(storedToken);
      // Restore emulation state from sessionStorage
      const emulateId = sessionStorage.getItem(EMULATE_KEY);
      if (emulateId) {
        networkManager.setEmulateUser(emulateId);
      }
      getCurrentUser()
        .then((userData) => {
          setUser(userData);
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        setTokens,
        can,
        isEmulating: !!emulatingYumId,
        startEmulation,
        stopEmulation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
